<script setup>
import { useCommonStore } from '../stores/common';
const store = useCommonStore();
const { setSidebarPhoto, setPageColor } = store;

const route = useRoute();
const gtm = useGtm();
const { t, locale } = useI18n();

const setI18nParams = useSetI18nParams();

import eventDateQuery from '~/assets/gql/event-date.gql';
const { data } = await useAsyncQuery(eventDateQuery, { id: route.params.dateId });

const eventDate = computed(() => data.value?.eventDate);
const event = computed(() => data.value?.eventDate.event);

const currentDate = computed(() => event.value?.dates.find((date) => date.id == route.params.dateId));

const bgColor = computed(() => event.value?.page_background_color);
const content = computed(() => (event.value && event.value?.content ? JSON.parse(event.value?.content).blocks : []));

const breadcrumbs = () => {
  const id = eventDate.value ? eventDate.value.event.id : null;
  const slug = eventDate.value ? eventDate.value.event.slug[locale] : null;
  return [
    {
      title: t('pages.events.breadcrumbs-title'),
      route: 'events',
    },
    {
      title: t('pages.event-detail.breadcrumbs-title'),
      route: { name: 'event-id-slug', params: { id, slug } },
    },
    { title: t('pages.event-date.breadcrumbs-title') },
  ];
};

// const contacts = () => {
//   return eventDate.value.contacts.length ? eventDate.value.contacts : eventDate.value.event.contacts;
// };
// const content = () => {
//   if (!eventDate.value) {
//     return [];
//   } else if (eventDate.value.content) {
//     return JSON.parse(eventDate.value.content);
//   } else if (eventDate.value.event.content) {
//     return JSON.parse(eventDate.value.event.content);
//   } else {
//     return [];
//   }
// };

store.setPageColor(bgColor.value);
onMounted(() => {
  store.setPageColor(bgColor.value);
  gtm.trackEvent({
    event: 'eventDetail_date',
    eventId: route.params.id,
    url: window.location.href.replace(window.location.origin, ''),
  });
  // mobileVersioCheck();
  // intervalId.value = setInterval(onResize, 250);
  // addEventListener('resize', this.onResize);
});
onBeforeRouteLeave(() => {
  store.setPageColor(null);
});

// watch(
//   () => eventDate.value,
//   () => {
//     if (eventDate && eventDate.event && eventDate.event.cropped_detail_image) {
//       setSidebarPhoto(eventDate.event.cropped_detail_image);
//     }
//   }
// );

// onUnmounted(() => {
//   setSidebarPhoto(null);
//   clearInterval(intervalId.value);
// });

// const mobileVersioCheck = () => {
//   mobileVersion.value = window.innerWidth < 992;
// };

// const onResize = () => {
//   mobileVersioCheck();
// };

setI18nParams({
  en: {
    slug: event?.value?.slug?.en,
  },
  cs: {
    slug: event?.value?.slug?.cs,
  },
});

function getBuilding(obj) {
  if (obj && obj.buildings) {
    return obj.buildings;
  } else if (event.value && event.value?.buildings) {
    return event.value?.buildings;
  } else {
    return null;
  }
}

useHeadData({
  title: event.value
    ? event.value?.seo_meta_title
      ? t('pages.event-detail.term-detail') + ' - ' + event.value?.seo_meta_title
      : t('pages.event-detail.term-detail') + ' - ' + event.value?.name
    : t('pages.event-detail.term-detail'),
  description: event.value?.seo_meta_description || null,
  keywords: event.value?.seo_meta_keywords || null,
  image: event.value?.image_detail.url || null,
  url: route.fullPath,
});
</script>

<template>
  <main v-if="eventDate" :style="{ backgroundColor: bgColor }" class="main">
    <div class="event-detail">
      <div class="event-detail__header">
        <Breadcrumbs :items="breadcrumbs()" />

        <div class="event-detail__hero mb-4">
          <img
            :src="useGetImageUrl(event.image_mobile || event.image_detail?.url || event.image_list?.url || '/img/dummy/ngp.png', 600)"
            class="event-detail__hero-image event-detail__hero-image--mobile"
            :alt="event.image_mobile_description || event.image_detail_description || event.image_listl_description || event.name"
          />
          <img
            :src="useGetImageUrl(event.image_detail?.url || event.image_mobile || event.image_list?.url || '/img/dummy/ngp.png', 600)"
            class="event-detail__hero-image event-detail__hero-image--desktop"
            :alt="event.image_detail_description || event.image_mobile_description || event.image_list_description || event.name"
          />
        </div>

        <h1 v-if="eventDate.name || eventDate.event.name" class="event-detail__heading heading container-max-content" v-html="eventDate.name || eventDate.event.name" />

        <p v-if="eventDate.event.program_type || eventDate.event.school_degree_type" class="text text--2 mt-2">
          <template v-if="eventDate.event.program_type">
            <strong>{{ t('pages.event-detail.program_type') }}: </strong>
            <template v-for="(type, key) in eventDate.event.program_type"><template v-if="key > 0">, </template>{{ t('pages.event-detail.program_type.' + type) }}</template>
          </template>
          <br v-if="eventDate.event.program_type && eventDate.event.school_degree_type" />
          <template v-if="eventDate.event.school_degree_type">
            <strong>{{ t('pages.event-detail.school_degree_type') }}: </strong>
            <template v-for="(type, key) in eventDate.event.school_degree_type"><template v-if="key > 0">, </template>{{ t('pages.event-detail.school_degree_type.' + type) }}</template>
          </template>
        </p>
      </div>

      <div class="container-max-content">
        <div class="event-detail__meta">
          <EventDateStrip
            :id="event.id"
            :reservation-type="event.reservation_type"
            :date="currentDate || null"
            :time="currentDate?.start_at?.timestamp || null"
            :time-stamp-end="currentDate?.end_at?.timestamp || null"
            :locations="!eventDate.event.online ? getBuilding(eventDate.event.buildings) : null"
            :no-link="true"
            :no-button="eventDate.event.admission.is_hidden"
            :event-name="event.name"
            :category-id="useGetCategoryId(eventDate.event)"
            :bold="true"
            :type="event.type"
            :duration-display="event?.display_duration || false"
            :hide-building-opening="event.event?.hide_building_opening || null"
          />
        </div>

        <DynamicPageContent :content="content" class="mt-4" />
        <ContactList v-if="eventDate.event.contacts.length" :contacts="eventDate.event.contacts" />

        <div v-if="eventDate.event.partners.length" class="mt-5">
          <h2 class="heading heading--2 heading--thin">
            {{ t('pages.event-detail.partners.title') }}
          </h2>
          <LogoGrid :data="eventDate.event.partners" class="mt-3" />
        </div>

        <DocumentList v-if="eventDate.event.documents.length" :documents="eventDate.event.documents" />

        <div v-if="eventDate.event.related.length" class="mt-5">
          <h2 class="heading heading--2 heading--thin">
            {{ t('pages.event-detail.related.title') }}
          </h2>
          <EventGrid :data="eventDate.event.related" class="mt-3" />
        </div>
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.event-detail {
  &__header {
    padding-bottom: 40px;
    @include mq($sm) {
      display: flex;
      flex-flow: column;
      margin-top: #{$mainPaddingTop * -1};
      min-height: 100vh;
      padding-top: $mainPaddingTop;
    }

    @include mq($lg) {
      margin-top: #{$mainLgPaddingTop * -1};
      padding-top: $mainLgPaddingTop;
    }
  }

  &__heading {
    flex-grow: 0;
  }

  &__meta {
    border-bottom: 1px solid $black;
    border-top: 1px solid $black;
    margin-bottom: 40px;
    margin-top: 40px;
    // padding-bottom: 15px;

    &:first-child {
      margin-top: 0;
    }

    .event-detail__extra-date {
      &:last-child {
        border-bottom: 0;
      }
    }
  }

  &__hero {
    overflow: hidden;
    @include mq($sm) {
      flex-grow: 1;
      min-height: 50vh;
      // overflow-x: auto;
      position: relative;
    }

    @include mq($md) {
      min-height: 0;
    }
  }

  &__hero-image {
    display: block;
    width: 100%;
    overflow: hidden;

    @include mq($sm) {
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      object-position: left top;
      object-fit: contain;
      width: auto;
    }
    @include mq($lg) {
      object-position: center;
      object-fit: cover;
    }
  }

  &__tags {
  }

  &__tags-title {
    font-weight: 500;
    text-transform: uppercase;
  }

  &__tags-list {
    display: inline-block;
  }

  &__tag {
    display: inline-block;

    &:after {
      content: ', ';
    }

    &:last-child {
      &:after {
        display: none;
      }
    }
  }

  &__people {
  }

  &__person {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__fb {
    a {
      .facebook-text {
        text-decoration: underline;
      }

      &:hover {
        .facebook-text {
          text-decoration: none;
        }
      }
    }
    span {
      display: inline-block;
      vertical-align: middle;
    }
    .icon-facebook {
      margin-right: 12px;
      font-size: 32px;
    }
  }
}

.event-detail__hero-image--desktop {
  @media (max-width: 992px) {
    display: none;
  }
}

.event-detail__hero-image--mobile {
  @media (min-width: 993px) {
    display: none;
  }
}
</style>
